import { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { fmtDate, ROLE_LABELS } from '../../lib/utils';
import { UserMultiSelect } from '../UserMultiSelect';
import { HtmlView } from '../RichTextEditor';
import { toast } from 'sonner';
import {
  Send, Paperclip, Loader2, CheckCircle2, RotateCcw, MessageSquareWarning, FileText, Download,
  History, ClipboardCheck, UserPlus, CalendarClock, MessageSquare, Upload, Sparkles, Ban, PlayCircle,
} from 'lucide-react';

export const TASK_STATUSES = [
  { value: 'not_started', label: 'Not Started', cls: 'bg-slate-100 text-slate-700' },
  { value: 'in_progress', label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  { value: 'waiting_info', label: 'Waiting on Information', cls: 'bg-amber-100 text-amber-700' },
  { value: 'waiting_approval', label: 'Waiting for Approval', cls: 'bg-violet-100 text-violet-700' },
  { value: 'on_hold', label: 'On Hold', cls: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'approved', label: 'Approved', cls: 'bg-emerald-200 text-emerald-800' },
  { value: 'reopened', label: 'Reopened', cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'cancelled', label: 'Cancelled', cls: 'bg-rose-100 text-rose-600' },
];
export const statusOf = (v) => TASK_STATUSES.find((s) => s.value === v) || TASK_STATUSES[0];

const EVENT_ICONS = {
  created: Sparkles, assigned: UserPlus, status_changed: PlayCircle, comment_added: MessageSquare,
  file_uploaded: Upload, completed: ClipboardCheck, approved: CheckCircle2,
  revision_requested: MessageSquareWarning, reopened: RotateCcw, due_date_changed: CalendarClock,
};

const fmtWhen = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} • ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const KIND_STYLE = {
  completion: 'border-emerald-200 bg-emerald-50',
  review: 'border-amber-200 bg-amber-50',
  file: 'border-slate-200 bg-white',
  comment: 'border-slate-200 bg-white',
};

function AttachmentChip({ att }) {
  const dl = async () => {
    try {
      const r = await api.get(att.file_path.replace('/api', ''), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };
  return (
    <button onClick={dl} className="flex items-center gap-2 bg-slate-100 hover:bg-magenta/10 hover:text-magenta rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 transition" data-testid={`attachment-${att.id}`}>
      <FileText className="w-3.5 h-3.5" /> {att.file_name}
      <Download className="w-3 h-3 opacity-60" />
      <span className="text-slate-400">{(att.size / 1024).toFixed(0)} KB</span>
    </button>
  );
}

export default function TaskDetailSheet({ taskId, open, onOpenChange, users, currentUser, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('journal');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completion, setCompletion] = useState({ completion_summary: '', challenges: '', recommendations: '', comments: '' });
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionNote, setRevisionNote] = useState('');
  const fileRef = useRef(null);
  const threadRef = useRef(null);

  const isAdmin = ['leadership', 'director'].includes(currentUser?.role);
  const task = detail?.task;

  const load = async () => {
    if (!taskId) return;
    try {
      const r = await api.get(`/tasks/${taskId}`);
      setDetail(r.data);
    } catch {
      toast.error('Task not found');
      onOpenChange(false);
    }
  };

  useEffect(() => { if (open && taskId) { setTab('journal'); load(); } /* eslint-disable-next-line */ }, [open, taskId]);
  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight }); }, [detail?.comments?.length]);

  const refresh = () => { load(); onChanged?.(); };

  const changeStatus = async (status) => {
    if (status === task.status) return;
    if (status === 'completed') {
      setCompletion({ completion_summary: '', challenges: '', recommendations: '', comments: '' });
      setCompleteOpen(true);
      return;
    }
    try {
      await api.post(`/tasks/${taskId}/status`, { status });
      toast.success('Status updated');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const submitCompletion = async () => {
    if (!completion.completion_summary.trim()) return toast.error('Completion summary is required');
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${taskId}/status`, { status: 'completed', ...completion });
      setCompleteOpen(false);
      toast.success(r.data.status === 'waiting_approval' ? 'Completed — sent for manager approval' : 'Task completed');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const sendComment = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { content: msg });
      setMsg('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (msg.trim()) form.append('note', msg);
      await api.post(`/tasks/${taskId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('');
      toast.success('File attached');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const review = async (action, comments = '') => {
    try {
      await api.post(`/tasks/${taskId}/review`, { action, comments });
      setRevisionOpen(false);
      setRevisionNote('');
      toast.success(action === 'approve' ? 'Task approved' : action === 'reopen' ? 'Task reopened' : 'Revision requested');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const updateField = async (patch) => {
    try {
      await api.put(`/tasks/${taskId}`, { ...task, ...patch });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  if (!task) return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="w-full sm:max-w-2xl"><SheetHeader><SheetTitle className="sr-only">Loading task</SheetTitle></SheetHeader></SheetContent></Sheet>;

  const st = statusOf(task.status);
  const assignee = users.find((u) => u.id === task.assigned_to);
  const canReview = isAdmin && ['completed', 'waiting_approval', 'approved'].includes(task.status);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="task-detail-sheet">
        <div className="p-6 pb-4 border-b border-slate-100 bg-slate-50/60">
          <SheetHeader>
            <SheetTitle className="font-heading text-navy text-xl pr-8" data-testid="task-detail-title">{task.title}</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Assigned To</Label>
              <UserMultiSelect
                users={users}
                value={task.assignees?.length ? task.assignees : (task.assigned_to ? [task.assigned_to] : [])}
                onChange={(v) => v.length && updateField({ assignees: v, assigned_to: v[0] })}
                testId="task-detail-assignees"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Status</Label>
              <Select value={task.status} onValueChange={changeStatus}>
                <SelectTrigger className="h-9 mt-0.5" data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.filter((s) => s.value !== 'approved' || task.status === 'approved').map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Priority</Label>
              <Select value={task.priority} onValueChange={(v) => updateField({ priority: v })}>
                <SelectTrigger className="h-9 mt-0.5" data-testid="task-detail-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Due Date</Label>
              <Input type="date" className="h-9 mt-0.5" value={(task.due_date || '').slice(0, 10)}
                onChange={(e) => updateField({ due_date: e.target.value ? new Date(`${e.target.value}T17:00:00`).toISOString() : null })}
                data-testid="task-detail-due" />
            </div>
          </div>
          {task.notes && (
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Description</Label>
              <div className="text-sm text-slate-600 mt-1"><HtmlView html={task.notes} testId="task-notes-view" /></div>
            </div>
          )}

          {canReview && (
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-3" data-testid="manager-review-panel">
              <div className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-2">Manager Review</div>
              <div className="flex gap-2 flex-wrap">
                {task.status !== 'approved' && (
                  <Button size="sm" onClick={() => review('approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="review-approve">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setRevisionOpen(true)} data-testid="review-request-revision">
                  <MessageSquareWarning className="w-4 h-4 mr-1.5" /> Request Revision
                </Button>
                <Button size="sm" variant="outline" onClick={() => review('reopen')} data-testid="review-reopen">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen Task
                </Button>
              </div>
            </div>
          )}

          {task.completion && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="completion-summary-card">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Completion Summary</div>
              <p className="text-sm text-slate-700">{task.completion.summary}</p>
              {task.completion.challenges && <p className="text-xs text-slate-500 mt-1"><strong>Challenges:</strong> {task.completion.challenges}</p>}
              {task.completion.recommendations && <p className="text-xs text-slate-500 mt-1"><strong>Recommendations:</strong> {task.completion.recommendations}</p>}
              <p className="text-[11px] text-slate-400 mt-1.5">By {task.completion.completed_by_name} · {fmtWhen(task.completion.completed_at)}</p>
            </div>
          )}
        </div>

        <div className="px-6 pt-4">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
            {[{ k: 'journal', l: 'Activity Journal', icon: MessageSquare }, { k: 'timeline', l: 'Timeline', icon: History }].map(({ k, l, icon: Icon }) => (
              <button key={k} onClick={() => setTab(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${tab === k ? 'bg-white shadow text-navy' : 'text-slate-500 hover:text-navy'}`}
                data-testid={`task-tab-${k}`}>
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
        </div>

        {tab === 'journal' && (
          <div className="flex flex-col" style={{ minHeight: '300px' }}>
            <div ref={threadRef} className="flex-1 px-6 py-4 space-y-3 max-h-[38vh] overflow-y-auto" data-testid="task-journal">
              {detail.comments.length === 0 && (
                <div className="text-center text-sm text-slate-400 py-8">No activity yet — start the conversation below.</div>
              )}
              {detail.comments.map((c) => (
                <div key={c.id} className={`border rounded-xl p-3 ${KIND_STYLE[c.kind] || KIND_STYLE.comment}`} data-testid={`journal-entry-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-full bg-navy text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                      {(c.user_name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-navy">{c.user_name}</span>
                      <span className="text-[10px] text-magenta font-semibold ml-2 uppercase tracking-wide">{ROLE_LABELS[c.user_role] || c.user_role}</span>
                      <div className="text-[11px] text-slate-400">{fmtWhen(c.created_at)}</div>
                    </div>
                    {c.kind === 'completion' && <span className="ml-auto text-[10px] font-bold text-emerald-600 uppercase">Completion Note</span>}
                    {c.kind === 'review' && <span className="ml-auto text-[10px] font-bold text-amber-600 uppercase">Manager Review</span>}
                  </div>
                  {c.content && <p className="text-sm text-slate-700 whitespace-pre-wrap ml-9">{c.content}</p>}
                  {c.attachments?.length > 0 && (
                    <div className="ml-9 mt-2 flex flex-wrap gap-2">
                      {c.attachments.map((a) => <AttachmentChip key={a.id} att={a} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-white sticky bottom-0">
              <div className="flex gap-2 items-end">
                <Textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder="Add a note, progress update, or question…"
                  rows={2}
                  className="resize-none"
                  data-testid="journal-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendComment(); }}
                />
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.zip,.csv,.txt"
                  onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="journal-file-input" />
                <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={busy} title="Attach file" data-testid="journal-attach">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button onClick={sendComment} disabled={busy || !msg.trim()} className="bg-magenta hover:bg-magenta/90" size="icon" data-testid="journal-send">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === 'timeline' && (
          <div className="px-6 py-4 space-y-0" data-testid="task-timeline">
            {detail.events.map((e, i) => {
              const Icon = EVENT_ICONS[e.type] || History;
              return (
                <div key={e.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-magenta/10 text-magenta flex items-center justify-center shrink-0">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    {i < detail.events.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className="pb-5">
                    <div className="text-sm text-navy font-medium">{e.description}</div>
                    <div className="text-[11px] text-slate-400">{e.user_name} · {fmtWhen(e.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Completion dialog */}
        <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
          <DialogContent className="max-w-md" data-testid="completion-dialog">
            <DialogHeader>
              <DialogTitle>Complete Task</DialogTitle>
              <DialogDescription>A completion summary is required to close this task.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">What was completed? *</Label>
                <Textarea value={completion.completion_summary} onChange={(e) => setCompletion({ ...completion, completion_summary: e.target.value })} rows={3} data-testid="completion-summary-input" />
              </div>
              <div>
                <Label className="text-xs">Challenges encountered (optional)</Label>
                <Textarea value={completion.challenges} onChange={(e) => setCompletion({ ...completion, challenges: e.target.value })} rows={2} data-testid="completion-challenges" />
              </div>
              <div>
                <Label className="text-xs">Recommendations (optional)</Label>
                <Textarea value={completion.recommendations} onChange={(e) => setCompletion({ ...completion, recommendations: e.target.value })} rows={2} data-testid="completion-recommendations" />
              </div>
              <div>
                <Label className="text-xs">Additional comments (optional)</Label>
                <Textarea value={completion.comments} onChange={(e) => setCompletion({ ...completion, comments: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
              <Button onClick={submitCompletion} disabled={busy || !completion.completion_summary.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="completion-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Mark Completed
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revision dialog */}
        <Dialog open={revisionOpen} onOpenChange={setRevisionOpen}>
          <DialogContent className="max-w-md" data-testid="revision-dialog">
            <DialogHeader>
              <DialogTitle>Request Revision</DialogTitle>
              <DialogDescription>Explain what needs to change — the task will move back to In Progress and the assignee ({assignee?.full_name || '—'}) will be notified.</DialogDescription>
            </DialogHeader>
            <Textarea value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} rows={4} placeholder="What needs revision?" data-testid="revision-note" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevisionOpen(false)}>Cancel</Button>
              <Button onClick={() => review('request_revision', revisionNote)} disabled={!revisionNote.trim()} className="bg-magenta hover:bg-magenta/90" data-testid="revision-submit">
                Send Revision Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="h-4" />
        <div className="px-6 pb-6 text-[11px] text-slate-400">
          Status: <span className={`px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span> · Created {fmtDate(task.created_at)}
        </div>
      </SheetContent>
    </Sheet>
  );
}
